import React, { useState } from 'react'
import { TableComponent } from '../../components/table'
import { NUMBER_OF_ITEMS_PER_PAGE } from '../../constants'

import { Button } from '../../components/button'
import CountCard from '../../components/cards/CountCard'
import { Status } from '../../components/cards/statusTag'
import Icon from '../../components/icon'
import Dropdown from '../../components/inputs/dropdown'
import { SearchInput } from '../../components/inputs/searchInput'
import { AddTagModal } from '../../components/modal/app_modals/addTagModal'
import { ConfirmationModal } from '../../components/modal/app_modals/confirmationModal'
import { PageHeader } from '../../components/typography/PageHeader'
import { CountCardContainer } from '../../components/wrapper/CountCardsContainer'
import { getPageFromSkip, getSkipFromPage } from '../../helpers'
import { useModalControl } from '../../hooks/useModalControl'
import {
  useDeleteProductTagMutation,
  useGetAllProductTagsQuery,
} from '../../redux-toolkits/products/products.slice'
import { Tag } from '../../redux-toolkits/products/products.type'
import { QueryProps } from '../../redux-toolkits/shared/shared.type'
const Tags: React.FC = () => {
  const [query, setQuery] = useState<QueryProps>({
    skip: 0,
    limit: NUMBER_OF_ITEMS_PER_PAGE,
    search: undefined,
  })

  const [selectedTag, setSelectedTag] = useState<Tag>()
  const [mode, setMode] = useState<'create' | 'edit'>('create')

  const { setModalOpen, modalOpen, handleModalOpen, handleModalClose, modal } =
    useModalControl()

  const { data, isFetching } = useGetAllProductTagsQuery(
    {
      skip: query.skip,
      limit: query.limit,
      search: query.search,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const [deleteTag, { isLoading: deletingTag }] = useDeleteProductTagMutation()

  return (
    <div>
      <PageHeader title="Tags" />

      <div className="w-full gap-4 flex flex-wrap items-center justify-between">
        <div className="!w-full md:!max-w-[400px]">
          <SearchInput
            searchFunction={(search) => {
              setQuery({
                ...query,
                search: search === '' ? undefined : search,
                skip: 0,
              })
            }}
            placeholder="Search tags"
          />
        </div>

        <Button
          size="medium"
          label="Add Tag"
          primary
          onClick={() => {
            setMode('create')
            handleModalOpen('addTagModal')
          }}
        />
      </div>

      <CountCardContainer>
        <CountCard
          isCurrency={false}
          text="Total tags count"
          count={Number(data?.data.totalItems ?? 0)}
        />
      </CountCardContainer>

      <div className="w-full overflow-x-scroll">
        <TableComponent
          headers={['Name', 'Status', 'Actions']}
          rows={
            data?.data?.data.length
              ? data.data.data.map((singleTag) => {
                  return {
                    id: singleTag._id,
                    content: [
                      <button
                        key={`${singleTag._id}-name`}
                        className="w-full text-left hover:underline"
                        onClick={(): any => {
                          setMode('edit')
                          handleModalOpen('addTagModal')
                          setSelectedTag(singleTag)
                        }}
                      >
                        {singleTag.name}
                      </button>,
                      <Status
                        key={`${singleTag._id}-status`}
                        text={singleTag.isActive ? 'Active' : 'Inactive'}
                        type={singleTag.isActive ? 'success' : 'fail'}
                      />,
                      <Dropdown
                        key={`${singleTag._id}-controls`}
                        menuButton={
                          <Icon
                            id="ellipses"
                            height={18}
                            width={18}
                            className=""
                          />
                        }
                        menuItems={[
                          {
                            name: 'Edit',
                            value: 'edit',
                            onClick: () => {
                              setMode('edit')
                              handleModalOpen('addTagModal')
                              setSelectedTag(singleTag)
                            },
                          },
                          {
                            name: 'Delete',
                            value: 'delete',
                            className: '!text-danger',
                            onClick: () => {
                              handleModalOpen('confirmDeleteModal')
                              setSelectedTag(singleTag)
                            },
                          },
                        ]}
                      />,
                    ],
                  }
                })
              : []
          }
          name="tags-table"
          loading={isFetching}
          isEmpty={[undefined, 0].includes(data?.data?.data.length)}
          currentPage={getPageFromSkip(Number(query.skip), Number(query.limit))}
          totalDataCount={Number(data?.data.totalItems) ?? 0}
          pageLimit={Number(query.limit)}
          onPageChange={(number) => {
            setQuery({
              ...query,
              skip: getSkipFromPage(number, Number(query.limit)),
            })
          }}
        />
      </div>

      <AddTagModal
        open={modalOpen && modal === 'addTagModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
          setSelectedTag(undefined)
        }}
        onSuccessCallback={() => {
          setSelectedTag(undefined)
        }}
        mode={mode}
        tag={selectedTag}
      />

      <ConfirmationModal
        open={modalOpen && modal === 'confirmDeleteModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          setSelectedTag(undefined)
          handleModalClose()
        }}
        confirmText="Delete Tag"
        title="Confirm tag deletion"
        onConfirm={async () => {
          const resp = await deleteTag({
            id: selectedTag?._id || '',
          })

          if (resp?.data?.success) {
            setSelectedTag(undefined)
            handleModalClose()
          }
        }}
        message="Are you sure you want to delete this tag?"
        loading={deletingTag}
      />
    </div>
  )
}

export default Tags
